<script context="module">
  import { get } from "svelte/store";
  import { apiMethods } from "../Services/index.svelte";
  import {
    allGroups,
    EmailTypes,
    PDFTypes,
    prePopulateAccountOptions,
    savedPublicHolidays,
    SMSTypes,
  } from "../stores";
  import {
    cacheAccountPrePopulateSearchOptions,
    cacheAllGroups,
  } from "./utilCache.svelte";
  import {
    isObjectEmpty,
    showException,
    parseTemplateTypeName,
    showError,
    importGoogleMapScript,
  } from "./utilFunctions.svelte";

  let isLoadingEmailTypes = false,
    hasEmailTypesLoaded = false,
    isLoadingSMSTypes = false,
    hasSMSTypesLoaded = false,
    isLoadingPDFTypes = false,
    hasPDFTypesLoaded = false;

  export const loadEmailTypes = async (reload) => {
    if (isLoadingEmailTypes || (hasEmailTypesLoaded && !reload)) return;
    isLoadingEmailTypes = true;
    await apiMethods.getMethod
      .getTemplateTypes("EmailTemplate")
      .then((res) => {
        EmailTypes.set(organizeTypes(res));
        isLoadingEmailTypes = false;
        hasEmailTypesLoaded = true;
      })
      .catch((err) => {
        showException(err);
        isLoadingEmailTypes = false;
      });
  };

  export const getEmailTemplateTypeName = (id) => {
    const loadedTypes = get(EmailTypes);
    if (loadedTypes.some((type) => type.id == id))
      return loadedTypes.find((type) => type.id == id)?.type;
    else return "No templates found...";
  };

  export const loadSMSTypes = async (reload) => {
    if (isLoadingSMSTypes || (hasSMSTypesLoaded && !reload)) return;
    isLoadingSMSTypes = true;
    await apiMethods.getMethod
      .getTemplateTypes("SmsTemplate")
      .then((res) => {
        SMSTypes.set(organizeTypes(res));
        isLoadingSMSTypes = false;
        hasSMSTypesLoaded = true;
      })
      .catch((err) => {
        showException(err);
        isLoadingSMSTypes = false;
      });
  };

  export const getSmsTemplateTypeName = (id) => {
    const loadedTypes = get(SMSTypes);
    if (loadedTypes.some((type) => type.id == id))
      return loadedTypes.find((type) => type.id == id)?.type;
    else return "No templates found...";
  };

  export const loadPDFTypes = async (reload) => {
    if (isLoadingPDFTypes || (hasPDFTypesLoaded && !reload)) return;
    isLoadingPDFTypes = true;
    apiMethods.getMethod
      .getTemplateTypes("PdfTemplate")
      .then((res) => {
        PDFTypes.set(organizeTypes(res));
        isLoadingPDFTypes = false;
        hasPDFTypesLoaded = true;
      })
      .catch((err) => {
        showException(err);
        isLoadingPDFTypes = false;
      });
  };

  export const getPdfTemplateTypeName = (id) => {
    const loadedTypes = get(PDFTypes);
    if (loadedTypes.some((type) => type.id == id))
      return loadedTypes.find((type) => type.id == id)?.type;
    else return "No templates found...";
  };

  export const initDataLoader = () => {
    loadEmailTypes();
    loadPDFTypes();
    loadSMSTypes();
  };

  const organizeTypes = (types) => {
    const results = [];
    if (!types || !Array.isArray(types) || !types.length) return results;
    types.forEach((type) => {
      switch (type.typeId) {
        case 0:
          if (results.some((r) => r.id === 0)) return;
          results.push({
            id: 0,
            type: "Estimate",
          });
          break;
        case 1:
          if (results.some((r) => r.id === 1)) return;
          results.push({
            id: 1,
            type: "Invoice",
          });
          break;
        case 2:
          if (results.some((r) => r.id === 2)) return;
          results.push({
            id: 2,
            type: "Receipt",
          });
          break;
        default:
          results.push({
            id: type.typeId,
            type: parseTemplateTypeName(type.typeName),
          });
          break;
      }
    });
    return results;
  };

  // do not use if there's a direct api method to update encounter
  export const loadAndUpdatePatientEncounterFields = async (id, newValues) => {
    await apiMethods.getMethod
      .getPatientEncounterById(id)
      .then(async (ect) => {
        if (!ect.id) throw "Failed to load encounter";
        delete ect["billingData"];
        delete ect["estimates"];
        delete ect["invoices"];
        ect = {
          ...ect,
          ...newValues,
        };
        await apiMethods.putMethod.updatePatientEncounter(ect, false, "Load and update").then((res) => {
          if (res.error) throw res.error;
        });
      })
      .catch((err) => showError(err));
  };

  const loadPublicHolidaysByYear = async (year) => {
    const yearFilter = { "filter[year]": year };
    return await apiMethods.getMethod
      .getPublicHolidays(0, 9999, yearFilter)
      .then((res) => {
        if (res.error) throw res.error;
        const newSet = {
          ...get(savedPublicHolidays),
        };
        newSet[year] = res.results;

        savedPublicHolidays.set(newSet);
        return res.results;
      })
      .catch((err) => {
        console.log(err);
        showError(
          "Failed to load public holidays, please try again later or contact support",
        );
        return [];
      });
  };

  export const getPublicHolidays = async (start, end, filters) => {
    const filterPublicHolidays = (holidays, filters) => {
      if (filters["filter[state]"]) {
        holidays = holidays.filter(
          (day) => day.state === filters["filter[state]"],
        );
      }
      return {
        results: holidays,
      };
    };
    if (!filters || !filters["filter[year]"]) return [];
    if (
      filters["filter[year]"] &&
      get(savedPublicHolidays)[filters["filter[year]"]]
    ) {
      return filterPublicHolidays(
        get(savedPublicHolidays)[filters["filter[year]"]],
        filters,
      );
    }
    return await loadPublicHolidaysByYear(filters["filter[year]"]).then((res) =>
      filterPublicHolidays(res, filters),
    );
  };

  export const getPublicHolidaysFlat = async (filters) => {
    const filterPublicHolidays = (holidays, filters) => {
      if (filters["filter[state]"]) {
        holidays = holidays.filter(
          (day) => day.state === filters["filter[state]"],
        );
      }
      return holidays;
    };
    if (!filters || !filters["filter[year]"]) return [];
    if (
      filters["filter[year]"] &&
      get(savedPublicHolidays)[filters["filter[year]"]]
    ) {
      return filterPublicHolidays(
        get(savedPublicHolidays)[filters["filter[year]"]],
        filters,
      );
    }
    return await loadPublicHolidaysByYear(filters["filter[year]"]).then((res) =>
      filterPublicHolidays(res, filters),
    );
  };

  export const searchGoogleAddress = async (searchText) => {
    await importGoogleMapScript();
    if (!searchText) return [];

    let res = await apiMethods.getMethod.getAddressFromGoogle(searchText);

    if (res && res.predictions) return [...res.predictions];
  };

  export const updateAccountCalendarEvent = async (id, newValues) => {
    if (!id || isObjectEmpty(newValues)) return;
    let event = await apiMethods.getMethod
      .getEventById(id)
      .catch((err) => showError(err));

    if (event.error) return showError(err);

    event = {
      ...event,
      ...newValues,
    };

    await apiMethods.putMethod
      .updateAccountCalendarEvent(event)
      .catch((err) => showError(err));
  };

  export const searchGroupsFromCache = async (searchText) => {
    let allCachedGroups = get(allGroups);
    if (!allCachedGroups.length) await cacheAllGroups();
    allCachedGroups = get(allGroups);
    if (searchText) {
      searchText = String(searchText).toUpperCase();
      return allCachedGroups.filter(
        (g) =>
          g?.id == searchText ||
          g?.name?.toUpperCase().includes(searchText.toUpperCase()),
      );
    } else return allCachedGroups;
  };

  export const searchDoctorsFromCache = async (searchText) => {
    await cacheAccountPrePopulateSearchOptions();
    const doctors = get(prePopulateAccountOptions);

    let res = searchText
      ? doctors.filter(
          (d) =>
            d?.id == String(searchText).toUpperCase() ||
            g?.name?.toUpperCase().includes(searchText.toUpperCase()),
        )
      : doctors;

    return res.map((d) => ({
      ...d,
      displayName: `${d.fullName} ${d.preferredName ? "(" + d.preferredName + ")" : ""}`,
    }));
  };
</script>
